import {
  getFullDestination,
  withDestination,
  withoutDestination,
} from '@/helpers/destination/getSearchDestination'
import { getDestinationAncestries } from '@/helpers/destination/getAncestries'
import { toFacetQuery } from '@/helpers/search/facets'
import { getFacetsFilterByDestinationId } from '@/repository/strapi'
import type { Destination } from 'lc-repository/dist/entities'
import type { SearchDestination } from '@@/types/types'

export const useDestination = () => {
  const { $apiClient, $i18n } = useNuxtApp()
  const { userIsAdmin, userIsPartner } = useAuth()
  const config = useRuntimeConfig()

  const searchDestination =
    useState<Partial<SearchDestination>>('search-destination')

  const ancestries = computed(() =>
    getDestinationAncestries(searchDestination.value?.destination),
  )

  const isLp = computed(() => searchDestination.value?.isLp ?? false)

  const getFacetDesti = async ({
    slug,
    filter,
  }: {
    slug?: string
    filter?: string
  }) => {
    const locale = unref($i18n.locale)
    if (slug && filter) {
      const { destination, facets, metaTitle, metaDesc, seoTitle, indexation } =
        await withDestination({
          $apiClient,
          locale,
          params: { slug, filter },
        })

      return {
        destination,
        facets,
        indexation,
        metaDesc,
        metaTitle,
        seoTitle,
      }
    } else if (slug) {
      const { facets, metaTitle, metaDesc, seoTitle, indexation } =
        await withoutDestination({
          locale,
          slug,
        })

      return {
        destination: {} as SearchDestination['destination'],
        facets,
        indexation,
        metaDesc,
        metaTitle,
        seoTitle,
      }
    }
  }

  const getSearchDesti = async (params: {
    name?: string
    destinationId?: string
  }) => {
    let metaDesc = ''
    let metaTitle = ''
    let metaCoverPhotoUrl = ''
    let destination = {} as Destination
    const linksSeo = [] as SearchDestination['linksSeo']

    if (params.name) {
      const res = await getFullDestination({
        $apiClient,
        config,
        params: {
          slug: params.name,
        },
      })

      if (res?.destination?.id && !userIsAdmin.value && !userIsPartner.value) {
        const filterFacets = await getFacetsFilterByDestinationId(
          res.destination.id,
        )
          .then(
            (res) =>
              res.data?.map((d) => ({ id: d.id, ...d.attributes })) ?? [],
          )
          .catch(() => [])

        if (filterFacets.length) linksSeo.push(...filterFacets)
      }

      destination = res?.destination || {}
      metaCoverPhotoUrl = res?.destination?.seoPhotoUrl ?? ''
      metaDesc = res?.destination?.metaDescription ?? ''
      metaTitle = res?.destination?.metaTitle ?? ''
    } else if (params.destinationId) {
      const getDestinationQueryData = await getFullDestination({
        $apiClient,
        config,
        params: {
          id: params.destinationId,
        },
      })
      destination = getDestinationQueryData?.destination || {}
    }

    return {
      destination,
      linksSeo,
      lpContent: {} as SearchDestination['lpContent'],
      metaCoverPhotoUrl,
      metaDesc,
      metaTitle,
    }
  }

  const fetchSearchDesti = async ({
    loadDesti,
    loadFacetSeo,
  }: {
    loadDesti: { name?: string; destinationId?: string }
    loadFacetSeo?: { slug?: string; filter?: string }
  }): Promise<Partial<SearchDestination>> => {
    if (loadDesti?.name || loadDesti.destinationId) {
      const { destination, linksSeo, metaCoverPhotoUrl, metaDesc, metaTitle } =
        await getSearchDesti(loadDesti)

      return {
        collections: [],
        destination,
        destinationId: destination?.id,
        destinations: [],
        isDestinationPage: false,
        linksSeo,
        lpContent: {} as SearchDestination['lpContent'],
        metaCoverPhotoUrl,
        metaDesc,
        metaTitle,
        seoTitle: (destination?.searchTextTitle as unknown as string) || '',
      }
    } else if (loadFacetSeo) {
      const result = await getFacetDesti(loadFacetSeo)
      if (result) {
        const {
          destination,
          facets,
          metaDesc,
          metaTitle,
          seoTitle,
          indexation,
        } = result
        const queryOutput = toFacetQuery(unref($i18n.locale), facets)

        return {
          destination,
          destinationId: destination?.id,
          indexationPage: indexation,
          isDestinationPage: false,
          linksSeo: [],
          metaDesc,
          metaTitle,
          seoTitle,
          queryOutput,
        }
      }
    }

    return {} as Partial<SearchDestination>
  }

  return {
    ancestries,
    fetchSearchDesti,
    isLp,
    searchDestination,
  }
}
